import { useCallback, useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Fade,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { getIngredients } from "../../insumos/api/ingredientsApi";
import { getSuppliers } from "../../proveedores/api/suppliersApi";
import PaginatedCatalogAutocomplete from "./PaginatedCatalogAutocomplete";
import {
  isCanonicalPurchaseUnit,
  PURCHASE_UNIT_OPTIONS,
} from "../constants/purchaseUnits";

const BASE_UNITS = ["g", "ml", "unit", "package"];
const UNIT_LABELS = {
  g: "Gramos (g)",
  ml: "Mililitros (ml)",
  unit: "Unidad",
  package: "Paquete",
};

function normalizeIngredient(ingredient) {
  if (!ingredient?.id || ingredient.isActive === false) return null;
  const category =
    ingredient.category?.name ||
    ingredient.ingredientCategory?.name ||
    ingredient.category ||
    "Sin categoría";
  return {
    ...ingredient,
    id: Number(ingredient.id),
    name: String(ingredient.name || "Sin nombre"),
    categoryName: String(category),
  };
}

function normalizeSupplier(supplier) {
  if (!supplier?.id || supplier.isActive === false) return null;
  return {
    ...supplier,
    id: Number(supplier.id),
    name: String(supplier.name || "Sin nombre"),
    phone: String(supplier.phone || "").trim() || null,
  };
}

function ingredientLabel(ingredient) {
  return ingredient?.name || "";
}

function supplierLabel(supplier) {
  return supplier?.name || "";
}

function normalizeDecimalInput(value) {
  return String(value ?? "").trim().replace(",", ".");
}

function formatDecimalForInput(value) {
  const normalized = normalizeDecimalInput(value);
  if (!/^\d+\.\d+$/.test(normalized)) return normalized;
  return normalized.replace(/\.?0+$/, "");
}

function validateDecimal(value, { scale, integerDigits, positive, label }) {
  const normalized = normalizeDecimalInput(value);
  if (!normalized) return { error: `${label} es obligatorio.` };
  const match = /^(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!match) {
    return { error: `${label} debe ser un número válido.` };
  }
  const integer = match[1].replace(/^0+(?=\d)/, "");
  const fraction = match[2] || "";
  const isZero = /^0+$/.test(integer) &&
    (!fraction || /^0+$/.test(fraction));
  if (integer.length > integerDigits || fraction.length > scale) {
    return {
      error: `${label} admite hasta ${integerDigits} enteros y ${scale} decimales.`,
    };
  }
  if (positive && isZero) return { error: `${label} debe ser mayor a cero.` };
  return { value: normalized };
}

function fieldSx() {
  return {
    "& .MuiOutlinedInput-root": {
      borderRadius: "16px",
      bgcolor: "logoblanco",
    },
    "& .MuiFormHelperText-root": {
      mx: 0.4,
      mt: 0.45,
      minHeight: 18,
      fontSize: 11.5,
      fontWeight: 600,
    },
  };
}

function FieldWithHelp({ label, help, children }) {
  return (
    <Stack
      direction="row"
      spacing={0.6}
      sx={{ alignItems: "flex-start", minWidth: 0 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      <Tooltip title={help} arrow enterTouchDelay={0}>
        <IconButton
          type="button"
          size="small"
          aria-label={`Ayuda sobre ${label.toLowerCase()}`}
          sx={{ mt: 0.8, color: "neutral.700" }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

const FIELD_HELP = {
  ingredient: "Insumo genérico al que corresponde esta presentación de compra.",
  supplier: "Proveedor al que se compra habitualmente esta presentación.",
  brand: "Marca comercial que identifica el producto comprado.",
  presentation: "Descripción visible del formato, por ejemplo: bolsa de 25 kg.",
  purchaseUnit: "Formato cerrado en el que el proveedor entrega el producto.",
  quantityPerUnit: "Cantidad de insumo base contenida en cada unidad de compra.",
  cost: "Precio total del pack o de la unidad de compra.",
  stockCountUnit: "Unidad que se utilizará al realizar el conteo físico de stock.",
};

export default function PurchaseItemFormModal({
  open,
  item = null,
  fixedIngredient = null,
  onClose,
  onSave,
}) {
  const submittingRef = useRef(false);
  const [ingredient, setIngredient] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [brand, setBrand] = useState("");
  const [presentation, setPresentation] = useState("");
  const [purchaseUnit, setPurchaseUnit] = useState("");
  const [quantityPerUnit, setQuantityPerUnit] = useState("");
  const [cost, setCost] = useState("");
  const [stockCountUnit, setStockCountUnit] = useState("package");
  const [inlineSupplier, setInlineSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [newSupplierPhone, setNewSupplierPhone] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const editing = Boolean(item?.id);

  useEffect(() => {
    if (!open) return;
    const initialIngredient = normalizeIngredient(fixedIngredient || item?.ingredient);
    setIngredient(initialIngredient);
    setSupplier(normalizeSupplier(item?.supplier));
    setBrand(item?.brand || "");
    setPresentation(item?.presentation || "");
    setPurchaseUnit(item?.purchaseUnit || "");
    setQuantityPerUnit(formatDecimalForInput(item?.quantityPerUnit));
    setCost(formatDecimalForInput(item?.cost));
    setStockCountUnit(item?.stockCountUnit || "package");
    setInlineSupplier(false);
    setNewSupplierName("");
    setNewSupplierPhone("");
    setFieldErrors({});
    setSubmitError("");
    setSaving(false);
    submittingRef.current = false;
  }, [fixedIngredient, item, open]);

  const loadIngredients = useCallback(
    ({ page, limit, search, signal }) =>
      getIngredients({
        page,
        limit,
        name: search,
        isActive: true,
        signal,
      }),
    []
  );

  const loadSuppliers = useCallback(
    ({ page, limit, search, signal }) =>
      getSuppliers({
        page,
        limit,
        search,
        isActive: true,
        signal,
      }),
    []
  );

  function clearError(field) {
    if (!fieldErrors[field]) return;
    setFieldErrors((current) => ({ ...current, [field]: "" }));
  }

  function handleIngredientChange(nextIngredient) {
    setIngredient(nextIngredient);
    clearError("ingredient");
  }

  function handleStockCountUnitChange(value) {
    setStockCountUnit(value);
    clearError("stockCountUnit");
  }

  function handleClose() {
    if (!submittingRef.current) onClose?.();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submittingRef.current) return;

    const errors = {};
    const normalizedBrand = brand.trim();
    const normalizedPresentation = presentation.trim();
    const normalizedPurchaseUnit = purchaseUnit.trim();
    const preservesHistoricalPurchaseUnit =
      editing &&
      !isCanonicalPurchaseUnit(item?.purchaseUnit) &&
      normalizedPurchaseUnit === String(item?.purchaseUnit || "").trim();
    const normalizedSupplierName = newSupplierName.trim();
    const normalizedSupplierPhone = newSupplierPhone.trim();

    if (!ingredient) errors.ingredient = "Seleccioná un insumo activo.";
    if (!normalizedBrand) errors.brand = "La marca es obligatoria.";
    else if (normalizedBrand.length > 150) errors.brand = "Máximo 150 caracteres.";
    if (!normalizedPresentation) errors.presentation = "La presentación es obligatoria.";
    else if (normalizedPresentation.length > 200) {
      errors.presentation = "Máximo 200 caracteres.";
    }
    if (!normalizedPurchaseUnit) errors.purchaseUnit = "La unidad de compra es obligatoria.";
    else if (!isCanonicalPurchaseUnit(normalizedPurchaseUnit) && !preservesHistoricalPurchaseUnit) {
      errors.purchaseUnit = "Seleccioná una unidad de compra de la lista.";
    }
    if (inlineSupplier) {
      if (!normalizedSupplierName) {
        errors.newSupplierName = "El nombre del proveedor es obligatorio.";
      } else if (normalizedSupplierName.length > 200) {
        errors.newSupplierName = "Máximo 200 caracteres.";
      }
      if (normalizedSupplierPhone.length > 30) {
        errors.newSupplierPhone = "Máximo 30 caracteres.";
      }
    } else if (!supplier) {
      errors.supplier = "Seleccioná un proveedor activo.";
    }

    if (!stockCountUnit) errors.stockCountUnit = "Seleccioná una unidad de conteo.";

    const quantity = validateDecimal(quantityPerUnit, {
      scale: 4,
      integerDigits: 8,
      positive: true,
      label: "La cantidad por unidad",
    });
    if (quantity.error) errors.quantityPerUnit = quantity.error;

    const normalizedCost = validateDecimal(cost, {
      scale: 2,
      integerDigits: 10,
      positive: false,
      label: "El costo",
    });
    if (normalizedCost.error) errors.cost = normalizedCost.error;

    setFieldErrors(errors);
    setSubmitError("");
    if (Object.keys(errors).length > 0) return;

    const payload = {
      ingredientId: ingredient.id,
      brand: normalizedBrand,
      presentation: normalizedPresentation,
      quantityPerUnit: quantity.value,
      cost: normalizedCost.value,
      stockCountUnit,
      ...(!preservesHistoricalPurchaseUnit
        ? { purchaseUnit: normalizedPurchaseUnit }
        : {}),
      ...(inlineSupplier
        ? {
            newSupplier: {
              name: normalizedSupplierName,
              phone: normalizedSupplierPhone || null,
            },
          }
        : { supplierId: supplier.id }),
    };

    submittingRef.current = true;
    setSaving(true);
    try {
      await onSave?.(payload);
    } catch (saveError) {
      setSubmitError(
        saveError?.message ||
          (saveError?.status === 409
            ? "Ya existe un ítem o proveedor con esos datos."
            : "No se pudo guardar el ítem de compra.")
      );
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      maxWidth={false}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          borderRadius: "30px",
          overflow: "visible",
          m: 1.5,
        },
      }}
      slotProps={{
        backdrop: {
          timeout: 180,
          sx: { bgcolor: "rgba(2, 8, 23, 0.45)", backdropFilter: "blur(3px)" },
        },
      }}
    >
      <Fade in={Boolean(open)} timeout={180}>
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          sx={{
            width: "min(94vw, 960px)",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            bgcolor: "neutral.300",
            borderRadius: "30px",
            p: { xs: 2, sm: 2.5 },
            boxShadow: "0px 20px 60px rgba(0,0,0,.28)",
            overflow: "hidden",
          }}
        >
          <Stack spacing={1.8} sx={{ minHeight: 0 }}>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "space-between" }}
            >
              <Box>
                <Typography sx={{ color: "primary.900", fontSize: 20, fontWeight: 800 }}>
                  {editing ? "Editar ítem de compra" : "Agregar ítem de compra"}
                </Typography>
                <Typography sx={{ color: "neutral.700", fontSize: 12.5, fontWeight: 600 }}>
                  {editing
                    ? "Actualizá la presentación comercial y sus equivalencias."
                    : "Completá los datos de la presentación que comprás al proveedor."}
                </Typography>
              </Box>
              <IconButton
                type="button"
                aria-label="Cerrar modal"
                disabled={saving}
                onClick={handleClose}
              >
                <CloseIcon sx={{ color: "primary.900" }} />
              </IconButton>
            </Stack>

            <Box sx={{ minHeight: 0, overflowY: "auto", pr: 0.4 }}>
              <Stack spacing={1.5}>
                <Box
                  sx={{
                    p: { xs: 1.6, sm: 2.2 },
                    borderRadius: "22px",
                    bgcolor: "neutral.50",
                    boxShadow: "inset 0px 0px 3px rgba(142,142,159,.35)",
                  }}
                >
                  <Stack spacing={1.4}>
                    <Box>
                      <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 800 }}>
                        Insumo y proveedor
                      </Typography>
                      <Typography sx={{ color: "neutral.700", fontSize: 11.5, fontWeight: 600 }}>
                        Elegí el insumo y quién provee esta presentación.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 1.4,
                      }}
                    >
                      <FieldWithHelp label="Insumo" help={FIELD_HELP.ingredient}>
                        <PaginatedCatalogAutocomplete
                          active={Boolean(open)}
                          label="Insumo"
                          value={ingredient}
                          onChange={handleIngredientChange}
                          loadPage={loadIngredients}
                          normalizeOption={normalizeIngredient}
                          getOptionLabel={ingredientLabel}
                          getOptionDescription={(option) =>
                            `${option.categoryName} · ${
                              UNIT_LABELS[option.baseUnit] || option.baseUnit
                            }`
                          }
                          disabled={saving || Boolean(fixedIngredient)}
                          error={fieldErrors.ingredient}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              <Box>
                                <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 800 }}>
                                  {option.name}
                                </Typography>
                                <Typography sx={{ color: "neutral.700", fontSize: 11.5, fontWeight: 600 }}>
                                  {option.categoryName} · {UNIT_LABELS[option.baseUnit] || option.baseUnit}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        />
                      </FieldWithHelp>

                      <Stack direction="row" spacing={0.8} sx={{ alignItems: "flex-start" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <FieldWithHelp label="Proveedor" help={FIELD_HELP.supplier}>
                            {inlineSupplier ? (
                              <Stack spacing={1.1}>
                                <TextField
                                  required
                                  fullWidth
                                  label="Nombre del nuevo proveedor"
                                  value={newSupplierName}
                                  disabled={saving}
                                  error={Boolean(fieldErrors.newSupplierName)}
                                  helperText={fieldErrors.newSupplierName || " "}
                                  onChange={(event) => {
                                    setNewSupplierName(event.target.value);
                                    clearError("newSupplierName");
                                  }}
                                  slotProps={{ htmlInput: { maxLength: 200 } }}
                                  sx={fieldSx()}
                                />
                                <TextField
                                  fullWidth
                                  label="Teléfono del nuevo proveedor"
                                  value={newSupplierPhone}
                                  disabled={saving}
                                  error={Boolean(fieldErrors.newSupplierPhone)}
                                  helperText={fieldErrors.newSupplierPhone || " "}
                                  onChange={(event) => {
                                    setNewSupplierPhone(event.target.value);
                                    clearError("newSupplierPhone");
                                  }}
                                  slotProps={{ htmlInput: { maxLength: 30 } }}
                                  sx={fieldSx()}
                                />
                              </Stack>
                            ) : (
                              <PaginatedCatalogAutocomplete
                                active={Boolean(open)}
                                label="Proveedor"
                                value={supplier}
                                onChange={(option) => {
                                  setSupplier(option);
                                  clearError("supplier");
                                }}
                                loadPage={loadSuppliers}
                                normalizeOption={normalizeSupplier}
                                getOptionLabel={supplierLabel}
                                getOptionDescription={(option) =>
                                  option.phone || "Sin teléfono"
                                }
                                disabled={saving}
                                error={fieldErrors.supplier}
                                renderOption={(props, option) => (
                                  <Box component="li" {...props} key={option.id}>
                                    <Box>
                                      <Typography sx={{ color: "primary.900", fontSize: 13, fontWeight: 800 }}>
                                        {option.name}
                                      </Typography>
                                      <Typography sx={{ color: "neutral.700", fontSize: 11.5, fontWeight: 600 }}>
                                        {option.phone || "Sin teléfono"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                              />
                            )}
                          </FieldWithHelp>
                        </Box>
                        <Tooltip
                          title={inlineSupplier ? "Elegir proveedor existente" : "Crear proveedor inline"}
                          arrow
                        >
                          <IconButton
                            type="button"
                            aria-label={inlineSupplier ? "Usar proveedor existente" : "Agregar proveedor inline"}
                            disabled={saving}
                            onClick={() => {
                              setInlineSupplier((current) => !current);
                              setSubmitError("");
                            }}
                            sx={{
                              mt: 0.7,
                              width: 40,
                              height: 40,
                              bgcolor: inlineSupplier ? "neutral.100" : "primary.500",
                              color: inlineSupplier ? "primary.900" : "logoblanco",
                              "&:hover": {
                                bgcolor: inlineSupplier ? "neutral.150" : "primary.600",
                              },
                            }}
                          >
                            {inlineSupplier ? <UndoIcon /> : <AddIcon />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>

                    <Divider sx={{ borderColor: "neutral.150" }} />

                    <Box>
                      <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 800 }}>
                        Unidades, equivalencias y costo
                      </Typography>
                      <Typography sx={{ color: "neutral.700", fontSize: 11.5, fontWeight: 600 }}>
                        Definí cómo se compra, se consume y se cuenta en stock.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.2,
                      }}
                    >
                      <FieldWithHelp label="Unidad de compra" help={FIELD_HELP.purchaseUnit}>
                        <FormControl fullWidth error={Boolean(fieldErrors.purchaseUnit)}>
                          <InputLabel id="purchase-unit-label" required>
                            Unidad de compra
                          </InputLabel>
                          <Select
                            labelId="purchase-unit-label"
                            inputProps={{ "aria-label": "Unidad de compra" }}
                            label="Unidad de compra"
                            value={purchaseUnit}
                            disabled={saving}
                            onChange={(event) => {
                              setPurchaseUnit(event.target.value);
                              clearError("purchaseUnit");
                            }}
                            sx={{ borderRadius: "16px", bgcolor: "logoblanco" }}
                          >
                            {!isCanonicalPurchaseUnit(purchaseUnit) && purchaseUnit ? (
                              <MenuItem value={purchaseUnit}>
                                {purchaseUnit} (valor histórico)
                              </MenuItem>
                            ) : null}
                            {PURCHASE_UNIT_OPTIONS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{fieldErrors.purchaseUnit || " "}</FormHelperText>
                        </FormControl>
                      </FieldWithHelp>

                      <FieldWithHelp label="Unidad de conteo" help={FIELD_HELP.stockCountUnit}>
                        <FormControl fullWidth error={Boolean(fieldErrors.stockCountUnit)}>
                          <InputLabel id="stock-count-unit-label">Unidad de conteo</InputLabel>
                          <Select
                            labelId="stock-count-unit-label"
                            inputProps={{ "aria-label": "Unidad de conteo" }}
                            label="Unidad de conteo"
                            value={stockCountUnit}
                            disabled={saving}
                            onChange={(event) => handleStockCountUnitChange(event.target.value)}
                            sx={{ borderRadius: "16px", bgcolor: "logoblanco" }}
                          >
                            {BASE_UNITS.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {UNIT_LABELS[unit]}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>{fieldErrors.stockCountUnit || " "}</FormHelperText>
                        </FormControl>
                      </FieldWithHelp>

                      <FieldWithHelp label="Costo" help={FIELD_HELP.cost}>
                        <TextField
                          fullWidth
                          required
                          label="Costo"
                          value={cost}
                          disabled={saving}
                          error={Boolean(fieldErrors.cost)}
                          helperText={fieldErrors.cost || " "}
                          onChange={(event) => {
                            setCost(event.target.value);
                            clearError("cost");
                          }}
                          slotProps={{
                            htmlInput: {
                              inputMode: "decimal",
                              "aria-label": "Costo",
                            },
                          }}
                          sx={fieldSx()}
                        />
                      </FieldWithHelp>

                      <FieldWithHelp label="Cantidad por unidad" help={FIELD_HELP.quantityPerUnit}>
                        <TextField
                          fullWidth
                          required
                          label="Cantidad por unidad"
                          value={quantityPerUnit}
                          disabled={saving}
                          error={Boolean(fieldErrors.quantityPerUnit)}
                          helperText={fieldErrors.quantityPerUnit || " "}
                          onChange={(event) => {
                            setQuantityPerUnit(event.target.value);
                            clearError("quantityPerUnit");
                          }}
                          slotProps={{
                            htmlInput: {
                              inputMode: "decimal",
                              "aria-label": "Cantidad por unidad",
                            },
                          }}
                          sx={fieldSx()}
                        />
                      </FieldWithHelp>
                    </Box>

                    <Divider sx={{ borderColor: "neutral.150" }} />

                    <Box>
                      <Typography sx={{ color: "primary.900", fontSize: 14, fontWeight: 800 }}>
                        Datos comerciales
                      </Typography>
                      <Typography sx={{ color: "neutral.700", fontSize: 11.5, fontWeight: 600 }}>
                        Identificá la marca y la presentación comercial.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.2,
                      }}
                    >
                      <FieldWithHelp label="Marca" help={FIELD_HELP.brand}>
                        <TextField
                          fullWidth
                          required
                          label="Marca"
                          value={brand}
                          disabled={saving}
                          error={Boolean(fieldErrors.brand)}
                          helperText={fieldErrors.brand || " "}
                          onChange={(event) => {
                            setBrand(event.target.value);
                            clearError("brand");
                          }}
                          slotProps={{
                            htmlInput: {
                              maxLength: 150,
                              "aria-label": "Marca",
                            },
                          }}
                          sx={fieldSx()}
                        />
                      </FieldWithHelp>
                      <FieldWithHelp label="Presentación" help={FIELD_HELP.presentation}>
                        <TextField
                          fullWidth
                          required
                          label="Presentación"
                          value={presentation}
                          disabled={saving}
                          error={Boolean(fieldErrors.presentation)}
                          helperText={fieldErrors.presentation || " "}
                          onChange={(event) => {
                            setPresentation(event.target.value);
                            clearError("presentation");
                          }}
                          slotProps={{
                            htmlInput: {
                              maxLength: 200,
                              "aria-label": "Presentación",
                            },
                          }}
                          sx={fieldSx()}
                        />
                      </FieldWithHelp>
                    </Box>
                  </Stack>
                </Box>

                {!!submitError && (
                  <Box role="alert" sx={{ px: 2, py: 1.2, borderRadius: "18px", bgcolor: "secondary.700" }}>
                    <Typography sx={{ color: "logoblanco", fontSize: 13, fontWeight: 800 }}>
                      No se pudo guardar
                    </Typography>
                    <Typography sx={{ color: "logoblanco", fontSize: 12, fontWeight: 600 }}>
                      {submitError}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{ alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between" }}
            >
              <Typography sx={{ color: "primary.900", fontSize: 12, fontWeight: 700 }}>
                Los campos marcados con * son obligatorios.
              </Typography>
              <Stack direction="row" spacing={1.2} sx={{ justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  onClick={handleClose}
                  disabled={saving}
                  sx={{
                    minWidth: 110,
                    height: 40,
                    borderRadius: "33px",
                    bgcolor: "neutral.100",
                    color: "primary.900",
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": { bgcolor: "neutral.150" },
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  sx={{
                    minWidth: 145,
                    height: 40,
                    borderRadius: "33px",
                    bgcolor: "primary.500",
                    color: "logoblanco",
                    textTransform: "none",
                    fontWeight: 800,
                    "&:hover": { bgcolor: "primary.600" },
                  }}
                >
                  {saving ? "Guardando..." : editing ? "Guardar cambios" : "Confirmar"}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Fade>
    </Dialog>
  );
}
